// src/App.js - Supabase完全対応版（セキュリティ強化）
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// 🔐 認証関連 - Supabase版のみ
import SupabaseLoginPage from "./SupabaseLoginPage";
import ChangePasswordPage from "./ChangePasswordPage";
import SupabaseProtectedRoute from "./components/SupabaseProtectedRoute";
import PasskeyPromptModal from "./components/PasskeyPromptModal";
import SettingsBackLink from "./settings/SettingsBackLink";

// 🔧 自社スタッフ機能 (Internal)
import { InternalStaffProvider } from './contexts/InternalStaffContext';
import InternalLogin from './internal/InternalLogin';
import InternalSelectOrg from './internal/InternalSelectOrg';
import InternalDashboard from './internal/InternalDashboard';
import InternalStaffManagement from './internal/InternalStaffManagement';
import InternalStaffBanner from './internal/InternalStaffBanner';

// 🏥 組織機能
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import OrganizationSelector from './components/OrganizationSelector';
import OrganizationSetup from './components/OrganizationSetup';
import OrganizationManagement from './components/OrganizationManagement';
import LoadingSpinner from './components/LoadingSpinner';

// 📱 メインページ - Supabase版を優先
import StaffHome from "./StaffHome";
import MobileShiftForm from "./MobileShiftForm";
import CalendarPage from "./pages/CalendarPage";

// 👨‍💼 管理者機能
import Admin from "./Admin";
import AdminEvents from "./AdminEvents";
import AdminEventList from "./AdminEventList";
import AutoAssignmentScreen from "./pages/AutoAssignmentScreen";
import DailyAssignmentScreen from "./pages/DailyAssignmentScreen";
import AdminProcedures from "./pages/AdminProcedures";
import NotificationAdminPage from "./pages/NotificationAdminPage";

// 🎓 教育機能
import EducationSettingsManager from "./components/EducationSettingsManager";
import EducationReportDashboard from "./components/EducationReportDashboard";

// 📚 手術・スキル関連
import WeeklyAssignments from "./WeeklyAssignments";
import ProcedureList from "./ProcedureList";
import ProcedureDetail from "./ProcedureDetail";
import ProcedureMaster from "./pages/ProcedureMaster";
import SurgeryRequest from "./pages/SurgeryRequest";
import SurgeryRequestCsvHelp from "./pages/SurgeryRequestCsvHelp";
import SkillChart from "./pages/SkillChart";
import SkillsList from "./pages/SkillsList";
import SkillsByLevelPage from "./pages/SkillsByLevelPage";
import StaffAssignmentImport from './pages/StaffAssignmentImport';
import StaffAssignmentDatabase from './pages/StaffAssignmentDatabase';

// ⚙️ 設定機能
import SettingsHome from "./settings";
import StaffSettings from "./settings/StaffSettings";
import DailyRoleSettings from "./settings/DailyRoleSettings";
import ShiftPatternSettings from "./settings/ShiftPatternSettings";
import SkillGroupSettings from "./settings/SkillGroupSettings";
import ShiftCompatibilitySettings from "./settings/ShiftCompatibilitySettings";
import AnnualHolidaySettings from "./settings/AnnualHolidaySettings";
import HolidaySettings from "./settings/HolidaySettings";
import PasswordPrefixSettings from './settings/PasswordPrefixSettings';
import ShiftDeadlineSettings from './settings/ShiftDeadlineSettings';
import JobTypeSettings from './settings/JobTypeSettings';
import InstructionNotificationSettings from './settings/InstructionNotificationSettings';

// 🌐 その他
import JoinGroupPage from "./pages/JoinGroupPage";
import EducationDashboardDemo from './EducationDashboardDemo';

//　ダッシュボード
import EducationDashboard from './EducationDashboard';

// 🎨 UI コンポーネント
import Header from "./components/ui/Header";
import FooterNav from "./components/ui/FooterNav";
import ScrollToTop from "./components/utils/ScrollToTop";

// 🔧 外部ライブラリ - スマホ対応DnD
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';

// 🆕 術式関連コンポーネント
import ProcedureStepEditor from "./components/ProcedureStepEditor";
import ProcedureManagement from "./components/ProcedureManagement";
import RoomLayoutDesigner from "./components/RoomLayoutDesigner";
import EquipmentMaterialMaster from "./components/EquipmentMaterialMaster";
import XRGuideSimulator from "./components/XRGuideSimulator";
import AnalyticsReports from "./components/AnalyticsReports";
import TrainingEducationMode from "./components/TrainingEducationMode";
import InventoryOrderingSystem from "./components/InventoryOrderingSystem";

// 🤖 AI機能
import AIChat from './components/AIChat';
import AIDashboard from './components/AIDashboard';

import DataMigration from './pages/DataMigration';
import StaffAssignmentEnhanced from "./pages/StaffAssignmentEnhanced";

import ChecklistManager from "./pages/ChecklistManager";

import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LinkSettings from "./settings/LinkSettings";

// 🛡️ 運営パネル（秘密 URL 配下）
import OperatorLogin from "./operator/OperatorLogin";
import OperatorRouteGuard from "./operator/OperatorRouteGuard";
import OperatorDashboard from "./operator/OperatorDashboard";
import OperatorUserManagement from "./operator/OperatorUserManagement";
import OperatorPasswordChange from "./operator/OperatorPasswordChange";
import OperatorDataFormats from "./operator/OperatorDataFormats";
import OperatorAuditLogs from "./operator/OperatorAuditLogs";

// 🔍 監査ログ (組織 admin 向け)
import AuditLogSettings from "./settings/AuditLogSettings";
// 🔐 パスキー設定
import PasskeySettings from "./settings/PasskeySettings";

// 📨 招待受諾ページ（公開）
import InviteAcceptPage from "./pages/InviteAcceptPage";

const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';


/**
 * 🎯 メインアプリケーションコンポーネント（認証後）
 */
const AuthenticatedApp = () => {
  const { 
    currentOrganization, 
    userOrganizations, 
    isLoading, 
    error, 
    currentUser,
    hasPermission 
  } = useOrganization();

  // 📱 デバイス検出によるDnDバックエンド選択
  const isTouchDevice = () => {
    return 'ontouchstart' in window || 
           navigator.maxTouchPoints > 0 || 
           navigator.msMaxTouchPoints > 0;
  };

  const backend = isTouchDevice() ? TouchBackend : HTML5Backend;
  const backendOptions = isTouchDevice() ? {
    enableMouseEvents: true,
    delayTouchStart: 150,
    delayMouseStart: 0,
    touchSlop: 5,
    ignoreContextMenu: true,
    enableHoverOutsideTarget: true,
    enableKeyboardEvents: false,
  } : {};

  // グローバルCSS追加
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .draggable-surgery-bar,
      .draggable-surgery-bar * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }

      .draggable-surgery-bar {
        cursor: grab;
        transition: transform 0.1s ease, box-shadow 0.1s ease;
        position: relative;
      }

      .draggable-surgery-bar:active {
        cursor: grabbing;
        transform: scale(1.02);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        z-index: 1000;
      }

      .organization-selector {
        background: #ffffff;
        border-bottom: 1px solid #e9ecef;
        z-index: 100;
      }

      .permission-denied {
        padding: 2rem;
        text-align: center;
        color: #6c757d;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 50vh;
        gap: 1rem;
      }

      .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        flex-direction: column;
        gap: 16px;
      }

      .error-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        flex-direction: column;
        gap: 16px;
        padding: 20px;
        text-align: center;
      }
    `;
    document.head.appendChild(style);

    return () => {
      try {
        document.head.removeChild(style);
      } catch (e) {
        // 無視
      }
    };
  }, []);

  // エラー状態
  if (error) {
    return (
      <div className="error-container">
        <h3>組織アクセスエラー</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          再読み込み
        </button>
      </div>
    );
  }

  return (
    <div className="dnd-provider">
      <DndProvider backend={backend} options={backendOptions}>
        <ScrollToTop />
        <InternalStaffBanner />
        <Header />
        <SettingsBackLink />
        <OrganizationSelector />
        <PasskeyPromptModal />

        <Routes>
          {/* 📱 メイン機能 */}
          <Route path="/home" element={<StaffHome />} />
          <Route path="/form" element={<MobileShiftForm />} />
          <Route path="/mobile-form" element={<MobileShiftForm />} />
          <Route path="/calendar" element={<CalendarPage />} />
          
          {/* 🏥 組織管理 */}
          <Route path="/organization-management" element={
            hasPermission('org:write') ? (
              <OrganizationManagement />
            ) : (
              <div className="permission-denied">
                <h3>アクセス権限がありません</h3>
                <p>この機能を使用するには管理者権限が必要です。</p>
              </div>
            )
          } />
          
          {/* 🤖 AI機能 */}
          <Route path="/ai-dashboard" element={<AIDashboard />} />
          <Route path="/ai-chat" element={<AIChat />} />
          
          {/* 👨‍💼 管理者機能 */}
          <Route path="/admin" element={
            hasPermission('schedule:write') ? (
              <Admin />
            ) : (
              <div className="permission-denied">
                <h3>アクセス権限がありません</h3>
                <p>シフト管理機能を使用するには適切な権限が必要です。</p>
              </div>
            )
          } />
          
          <Route path="/admin-events" element={<AdminEvents />} />
          <Route path="/admin-events-list" element={<AdminEventList />} />
          <Route path="/auto-assignment" element={<AutoAssignmentScreen />} />
          <Route path="/daily-assignment" element={<DailyAssignmentScreen />} />
          <Route path="/admin-procedures" element={<AdminProcedures />} />
          <Route path="/admin-notifications" element={<NotificationAdminPage />} />
          
          {/* 📋 配置・手術関連 */}
          <Route path="/assignments" element={<WeeklyAssignments />} />
          <Route path="/surgery-request" element={<SurgeryRequest />} />
          <Route path="/surgery-request/csv-help" element={<SurgeryRequestCsvHelp />} />
          <Route path="/staff-assignment-import" element={<StaffAssignmentImport />} />
          <Route path="/staff-assignment-db" element={<StaffAssignmentDatabase />} />
          
          {/* 🎓 教育機能 */}
          <Route path="/education-settings" element={
            hasPermission('schedule:write') ? (
              <EducationSettingsManager />
            ) : (
              <div className="permission-denied">
                <h3>アクセス権限がありません</h3>
              </div>
            )
          } />
          
          <Route path="/education-reports" element={
            hasPermission('schedule:read') ? (
              <EducationReportDashboard />
            ) : (
              <div className="permission-denied">
                <h3>アクセス権限がありません</h3>
              </div>
            )
          } />

          <Route path="/data-migrations" element={<DataMigration />} />

          {/* 📚 手順書・術式関連 */}
          <Route path="/procedures" element={<ProcedureList />} />
          <Route path="/procedures/:id" element={<ProcedureDetail />} />
          <Route path="/procedure-master" element={<ProcedureMaster />} />
          
          {/* 📊 スキル関連 */}
          <Route path="/skills" element={<SkillsList />} />
          <Route path="/skills/by-level" element={<SkillsByLevelPage />} />
          <Route path="/skills/chart/:staffId" element={<SkillChart />} />
          <Route path="/staff-assignment-enhanced" element={<StaffAssignmentEnhanced />} />
          
          {/* ⚙️ 設定機能 */}
          <Route path="/settings" element={<SettingsHome />} />
          <Route path="/settings/staff" element={<StaffSettings />} />
          <Route path="/settings/daily-roles" element={<DailyRoleSettings />} />
          <Route path="/settings/shift-patterns" element={<ShiftPatternSettings />} />
          <Route path="/settings/skill-groups" element={<SkillGroupSettings />} />
          <Route path="/settings/shift-compatibility" element={<ShiftCompatibilitySettings />} />
          <Route path="/settings/annual-holidays" element={<AnnualHolidaySettings />} />
          <Route path="/settings/holidays" element={<HolidaySettings />} />
          <Route path="/settings/links" element={<LinkSettings />} />
          <Route path="/settings/password-prefix" element={<PasswordPrefixSettings />} />
          <Route path="/settings/shift-deadline" element={<ShiftDeadlineSettings />} />
          <Route path="/settings/job-types" element={<JobTypeSettings />} />
          <Route path="/settings/instruction-notification" element={<InstructionNotificationSettings />} />
          <Route path="/settings/audit-logs" element={<AuditLogSettings />} />
          <Route path="/settings/passkey" element={<PasskeySettings />} />

          <Route path="/checklist-manager" element={<ChecklistManager />} />
          
          {/* 🌐 その他 */}
          <Route path="/join-group" element={<JoinGroupPage />} />

          {/* ダッシュボード */}
          <Route path="/education-dashboard" element={<EducationDashboard />} />
          <Route path="/education-dashboard-demo" element={<EducationDashboardDemo />} />

          {/* 🆕 術式関連 */}
          <Route path="/procedure-step-editor" element={
            <div className="p-6">
              <ProcedureStepEditor />
            </div>
          } />
          <Route path="/procedure-management" element={<ProcedureManagement />} />
          <Route path="/room-layout-designer" element={<RoomLayoutDesigner />} />
          <Route path="/equipment-material-master" element={<EquipmentMaterialMaster />} />
          <Route path="/xr-guide-simulator" element={<XRGuideSimulator />} />
          <Route path="/analytics-reports" element={<AnalyticsReports />} />
          <Route path="/training-education" element={<TrainingEducationMode />} />
          <Route path="/inventory-management" element={<InventoryOrderingSystem />} />
          <Route path="/inventory" element={<InventoryOrderingSystem />} />
          
          {/* 🚫 存在しないパス */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        
        {currentOrganization && <FooterNav />}
      </DndProvider>
    </div>
  );
};

/**
 * 🎯 ルートアプリケーションコンポーネント
 */
function App() {
  return (
    <OrganizationProvider>
      <InternalStaffProvider>
      <Router>
        <Routes>
          {/* 🔓 公開ページ（認証不要） */}
          <Route path="/" element={<SupabaseLoginPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/supabase-login" element={<Navigate to="/" replace />} />
          
          {/* ✅ パスワードリセット（認証不要） - 追加 */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* 🛡️ 運営パネル（秘密 URL 配下、独立した認証フロー） */}
          <Route path={`/${OPERATOR_PATH}/login`} element={<OperatorLogin />} />
          <Route
            path={`/${OPERATOR_PATH}`}
            element={
              <OperatorRouteGuard>
                <OperatorDashboard />
              </OperatorRouteGuard>
            }
          />
          <Route
            path={`/${OPERATOR_PATH}/users`}
            element={
              <OperatorRouteGuard>
                <OperatorUserManagement />
              </OperatorRouteGuard>
            }
          />
          <Route
            path={`/${OPERATOR_PATH}/password`}
            element={
              <OperatorRouteGuard>
                <OperatorPasswordChange />
              </OperatorRouteGuard>
            }
          />
          <Route
            path={`/${OPERATOR_PATH}/data-formats`}
            element={
              <OperatorRouteGuard>
                <OperatorDataFormats />
              </OperatorRouteGuard>
            }
          />
          <Route
            path={`/${OPERATOR_PATH}/audit-logs`}
            element={
              <OperatorRouteGuard>
                <OperatorAuditLogs />
              </OperatorRouteGuard>
            }
          />

          {/* 📨 招待受諾ページ（公開、認証不要） */}
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          {/* 🔧 自社スタッフ専用 (SupabaseProtectedRoute を通さない:
                 - /internal/login は公開ページ
                 - /internal/select-org, /internal/dashboard は自前で internal_staff 認証チェック) */}
          <Route path="/internal/login" element={<InternalLogin />} />
          <Route path="/internal/select-org" element={<InternalSelectOrg />} />
          <Route path="/internal/dashboard" element={<InternalDashboard />} />
          <Route path="/internal/staff-management" element={<InternalStaffManagement />} />

          {/* 🔒 パスワード変更（認証必要） */}
          <Route
            path="/change-password"
            element={
              <SupabaseProtectedRoute>
                <ChangePasswordPage />
              </SupabaseProtectedRoute>
            }
          />

          {/* 🔒 認証後のアプリ（すべて保護） */}
          <Route
            path="/*"
            element={
              <SupabaseProtectedRoute>
                <AuthenticatedApp />
              </SupabaseProtectedRoute>
            }
          />
        </Routes>
      </Router>
      </InternalStaffProvider>
    </OrganizationProvider>
  );
}

export default App;